import React, { PropsWithChildren, ReactElement } from 'react';
import { useTestId } from '../../hooks/useTestId/useTestId';
import ReactModal, { BaseModalProps } from 'react-overlays/Modal';
import styled, { css } from 'styled-components';
import { IBlurProps } from '../../fundamentals/blurs/blurs';
import { IShadowProps, SHADOW_TYPES, useShadowCss } from '../../fundamentals/shadows/shadows';
import { PxToRem, SIZES, PX_TO_REM } from '../../fundamentals/sizes/sizes';
import { usePanelBorderRadiusCss } from '../../fundamentals/sizes/borderRadius';
import { useZeroPaddingOnChildPsCss } from '../../components/Texts/Texts';
import { Icon, IIconProps } from '../../components/Icon/Icon';
import { IStatusIconProps } from '../../components/StatusIcon/StatusIcon';
import { CloseButton, ICloseButtonProps } from '../../components/CloseButton/CloseButton';
import { Z_INDICES } from '../../common/constants';
import { Overlay } from '../../components/Overlay/Overlay';
import { CtaButtonBar } from '../../components/CtaButtonBar/CtaButtonBar';
import { IAllowBodyStyleOverrideProps } from '../../common/interfaces';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { ResponsiveGapContainer, ResponsiveSpaceContainer } from '../../fundamentals/spaces/responsive/SpaceContainerResponsive';
import { ScrollingContainer } from '../../fundamentals/spaces/ScrollingContainer';
import { EORA_NUMBERS } from '../../fundamentals/numbers/eoraNumbers';
import { IButtonProps } from '../../components/Button/Button';
import { useWindowDimensions } from '../../hooks/useResponsive/useResponsive';
import { IModalAlert, IModalBadge, IModalButton, IModalIcon, IModalLinkButton, IModalStatusIcon } from './BaseModal';
import { ModalFooterButtons, ModalHeaderIcon, ModalTitle } from './BaseModal';
import { Badge } from '../../components/Badge/Badge';
import { Alert } from '../../components/Alert/Alert';

export const modalColours = ({ eoraCoreColours }: IColoursCollection) => ({
  modalColours: {
    background: eoraCoreColours.BACKGROUND_COLOR.PANEL_COLOR_BACKGROUND
  }
});

export type ModalSize = typeof SIZES.SMALL | typeof SIZES.MEDIUM | typeof SIZES.LARGE | number;

export interface IModalProps extends Omit<BaseModalProps, 'children'>, IBlurProps, IAllowBodyStyleOverrideProps {
  title?: string;
  size?: ModalSize;
  icon?: IModalIcon | IModalStatusIcon;
  closable?: boolean;
  badge?: IModalBadge;
  footerButtons?: (IModalButton | IModalLinkButton)[];
  /**
   * @description Automatically dock to bottom (sheet) on mobile (default false)
   */
  dockOnMobile?: boolean;
  alert?: IModalAlert;
  onClose?: () => void; // Note: controls the close button action
  showHeaderSeparator?: boolean;
  showFooterSeparator?: boolean;
  /**
   * @deprecated Avoid customisation
   */
  allowHeaderStyleOverride?: boolean;
  /**
   * @deprecated Prefer icon , offers automatic icon size change for mobile
   */
  preTitle?: ReactElement<IIconProps | IStatusIconProps>;
  /**
   * @deprecated Prefer footerButtons , offers automatic button size change for mobile
   */
  ctaButtons?: ReactElement<IButtonProps> | ReactElement<IButtonProps>[];
}

const modalWidth: Record<ModalSize, PxToRem> = {
  [SIZES.SMALL]: PX_TO_REM[EORA_NUMBERS.CORE.WIDTHS.MODAL_WIDTHS.SIZE_MODAL_WIDTH_SMALL],
  [SIZES.MEDIUM]: PX_TO_REM[EORA_NUMBERS.CORE.WIDTHS.MODAL_WIDTHS.SIZE_MODAL_WIDTH_MEDIUM],
  [SIZES.LARGE]: PX_TO_REM[EORA_NUMBERS.CORE.WIDTHS.MODAL_WIDTHS.SIZE_MODAL_WIDTH_LARGE]
};

export const useModalWrapperPositionCss = css`
  position: fixed;
  z-index: ${Z_INDICES.MODAL};
`;

const useModalMaxWidthCss = css`
  max-width: ${({ theme }) => `calc(100% - ${theme.spaces[16]} - ${theme.spaces[16]})`};
`;

export const useModalWrapperCss = css`
  ${useModalWrapperPositionCss};
  ${useModalMaxWidthCss};
`;

export const useModalWrapperCenteredCss = css`
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const ModalWrapper = styled(ReactModal)<{ $dock: boolean }>`
  ${({ $dock }) =>
    $dock
      ? css`
          ${useModalWrapperPositionCss};
          width: 100%;
          bottom: 0;
          right: 0;
          left: 0;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        `
      : css`
          ${useModalWrapperCss};
          ${useModalWrapperCenteredCss};
        `};
`;

const ModalContainer = styled(ResponsiveGapContainer)<IShadowProps & Pick<IModalProps, 'size'> & { $dock: boolean }>`
  ${useShadowCss};
  max-width: 100%;
  max-height: ${({ $dock }) => ($dock ? 'calc(100vh - 40px)' : '70vh')}; // NOTE: 40px assumed size of a phone's top bar (battery, clock etc.)
  background: ${({ theme }) => theme.modalColours.background};
  ${({ $dock }) => usePanelBorderRadiusCss($dock)};
  width: ${({ size, $dock }) => {
    if ($dock) return '100%';
    return typeof size === 'number' ? `${size}px` : modalWidth[size!];
  }};
`;

const ModalHeader = styled(ResponsiveGapContainer)<Pick<IModalProps, 'allowHeaderStyleOverride'>>`
  ${({ allowHeaderStyleOverride }) =>
    allowHeaderStyleOverride &&
    css`
      & > ${Icon} {
        height: ${({ theme }) => theme.sizes[SIZES.SMALL]};
        width: ${({ theme }) => theme.sizes[SIZES.SMALL]};
      }
    `}
`;

const HeaderCloseButton = styled(CloseButton)<ICloseButtonProps>`
  margin-left: auto;
`;

const ModalBody = styled(ResponsiveGapContainer)<Pick<IModalProps, 'allowBodyStyleOverride'>>`
  overflow-y: auto;
`;

const ModalContent = styled(ResponsiveGapContainer)<Pick<IModalProps, 'allowBodyStyleOverride'>>`
  ${({ allowBodyStyleOverride }) =>
    allowBodyStyleOverride &&
    css`
      ${useZeroPaddingOnChildPsCss};
    `}
`;

const ModalSeparator = styled(ResponsiveSpaceContainer)`
  width: 100%;
  height: ${({ theme }) => theme.sizes.offgrid1};
  background-color: ${({ theme }) => theme.colours.eoraCoreColours.BORDER_COLOR.COLOR_BORDER_DEFAULT};
`;

export const Modal: React.FC<PropsWithChildren<IModalProps>> = ({
  title,
  onClose,
  icon,
  blur = false,
  closable = true,
  badge,
  allowBodyStyleOverride = true,
  footerButtons,
  children,
  size = SIZES.MEDIUM,
  dockOnMobile = false,
  showHeaderSeparator = false,
  showFooterSeparator = false,
  alert,
  preTitle,
  allowHeaderStyleOverride = true,
  ctaButtons,
  ...rest
}) => {
  const { generateTestId } = useTestId(rest, 'Modal');

  const handleOnClose = () => (onClose ?? rest.onHide)?.();
  const { isMobile } = useWindowDimensions();
  const dock = !!isMobile && !!dockOnMobile;

  return (
    <ModalWrapper $dock={dock} renderBackdrop={(props: any) => <Overlay {...props} blur={blur} />} {...rest} {...generateTestId()}>
      <ModalContainer column size={size} shadow={SHADOW_TYPES.DEFAULT} $dock={dock} gap={false} {...generateTestId('Container')}>
        {(title || closable) && (
          <ModalHeader
            gap={'small'}
            gutter={'medium'}
            alignItems='center'
            allowHeaderStyleOverride={!icon && allowHeaderStyleOverride}
            {...generateTestId('Header')}
          >
            {!icon && preTitle}
            <ModalHeaderIcon isMobile={isMobile} icon={icon} />
            {badge && <Badge {...badge} size={isMobile ? SIZES.X_SMALL : SIZES.SMALL} />}
            <ModalTitle isMobile={isMobile} title={title} />
            {closable && <HeaderCloseButton onClick={handleOnClose} variant='black50' size={isMobile ? SIZES.XX_SMALL : SIZES.SMALL} minimal />}
          </ModalHeader>
        )}
        {showHeaderSeparator && <ModalSeparator gutterBottom={'medium'} gutterAsMargin {...generateTestId('HeaderSeparator')} />}
        <ModalBody gutter={'medium'} gutterTop={false} {...generateTestId('Body')} column>
          {alert && <Alert {...alert} {...generateTestId(alert.testId ?? 'Alert')} />}
          <ScrollingContainer>
            <ModalContent allowBodyStyleOverride={allowBodyStyleOverride} gap={'medium'} column>
              {children}
            </ModalContent>
          </ScrollingContainer>
        </ModalBody>

        {showFooterSeparator && <ModalSeparator gutterBottom={'medium'} gutterAsMargin {...generateTestId('FooterSeparator')} />}
        <CtaButtonBar
          gutter={'medium'}
          gutterTop={false}
          ctaButtons={<ModalFooterButtons footerButtons={footerButtons} ctaButtons={ctaButtons} />}
          {...generateTestId(`CTA`)}
        />
      </ModalContainer>
    </ModalWrapper>
  );
};
